import React, { useState, useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { adminAPI } from '../../../utils/api';

const AnalyticsTab = () => {
  const [overview, setOverview] = useState(null);
  const [popularPages, setPopularPages] = useState([]);
  const [timeSeries, setTimeSeries] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUserId, setFilterUserId] = useState('');
  const [filterVisitorId, setFilterVisitorId] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Separate effect for filtering recent activity
  useEffect(() => {
    fetchRecentActivity();
  }, [filterUserId, filterVisitorId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [overviewRes, pagesRes, timeSeriesRes] = await Promise.all([
        adminAPI.getAnalyticsOverview(),
        adminAPI.getPopularPages(7),
        adminAPI.getAnalyticsTimeSeries(14),
      ]);

      setOverview(overviewRes.data);
      setPopularPages(pagesRes.data);
      setTimeSeries(timeSeriesRes.data);

      // Fetch activity separately
      await fetchRecentActivity();
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      setActivityLoading(true);
      const params = { limit: 100 };
      if (filterUserId) params.userId = filterUserId;
      if (filterVisitorId) params.visitorId = filterVisitorId;

      const activityRes = await adminAPI.getRecentActivity(params);
      setRecentActivity(activityRes.data);
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleFilterByVisitor = (visitorId) => {
    setFilterVisitorId(visitorId);
    setFilterUserId(''); // Clear user filter
  };

  const handleFilterByUser = (userId) => {
    setFilterUserId(userId);
    setFilterVisitorId(''); // Clear visitor filter
  };

  const clearFilters = () => {
    setFilterUserId('');
    setFilterVisitorId('');
  };

  if (loading || !overview) {
    return (
      <Box sx={{ textAlign: 'center', padding: '48px', color: '#94A3B8' }}>
        Loading analytics...
      </Box>
    );
  }

  const stats = [
    {
      label: 'Unique Visitors',
      today: overview.uniqueVisitors.today,
      yesterday: overview.uniqueVisitors.yesterday,
      week: overview.uniqueVisitors.last7Days,
      total: overview.uniqueVisitors.total,
      color: '#3B82F6',
    },
    {
      label: 'Page Views',
      today: overview.pageViews.today,
      yesterday: overview.pageViews.yesterday,
      week: overview.pageViews.last7Days,
      total: overview.pageViews.total,
      color: '#8B5CF6',
    },
    {
      label: 'Anonymous Visitors',
      value: overview.uniqueVisitors.anonymous,
      color: '#64748B',
    },
    {
      label: 'Authenticated',
      value: overview.uniqueVisitors.authenticated,
      color: '#10B981',
    },
    {
      label: 'Conversion Rate',
      value: `${overview.conversionRate}%`,
      color: '#F59E0B',
    },
  ];

  return (
    <Box>
      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <StatCard stat={stat} />
          </Grid>
        ))}
      </Grid>

      {/* Traffic Chart */}
      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#3B82F6' }}>
          📈 Traffic Over Time (14 Days)
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={timeSeries}>
            <defs>
              <linearGradient id="visitorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="pageViewGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="date"
              stroke="#64748B"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis stroke="#64748B" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                background: '#1E293B',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#F1F5F9',
              }}
            />
            <Legend wrapperStyle={{ color: '#94A3B8' }} />
            <Area
              type="monotone"
              dataKey="uniqueVisitors"
              stroke="#3B82F6"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#visitorGradient)"
              name="Unique Visitors"
            />
            <Area
              type="monotone"
              dataKey="pageViews"
              stroke="#8B5CF6"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#pageViewGradient)"
              name="Page Views"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      <Grid container spacing={3}>
        {/* Popular Pages */}
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              background: '#1E293B',
              border: '1px solid #334155',
              borderRadius: '12px',
              padding: '24px',
            }}
          >
            <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#10B981' }}>
              🔥 Popular Pages (Last 7 Days)
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {popularPages.map((page, index) => (
                <Box
                  key={index}
                  sx={{
                    background: '#0F172A',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#F1F5F9', marginBottom: '4px' }}>
                      {page.path}
                    </Box>
                    <Box sx={{ fontSize: '12px', color: '#94A3B8' }}>
                      {page.uniqueVisitors} unique visitor{page.uniqueVisitors !== 1 ? 's' : ''}
                    </Box>
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 700, color: '#3B82F6' }}>
                    {page.views}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              background: '#1E293B',
              border: '1px solid #334155',
              borderRadius: '12px',
              padding: '24px',
            }}
          >
            <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px', color: '#F59E0B' }}>
              ⚡ Recent Activity
            </Box>

            {/* Filter Controls */}
            <Box sx={{ marginBottom: '16px' }}>
              <Box sx={{ fontSize: '12px', color: '#94A3B8', marginBottom: '8px' }}>
                Filter by user (click IDs below or paste here):
              </Box>
              <Box sx={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="Visitor ID"
                  value={filterVisitorId}
                  onChange={(e) => setFilterVisitorId(e.target.value)}
                  style={{
                    flex: 1,
                    background: '#0F172A',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#F1F5F9',
                    fontSize: '12px',
                    outline: 'none',
                  }}
                />
                <input
                  type="text"
                  placeholder="User ID"
                  value={filterUserId}
                  onChange={(e) => setFilterUserId(e.target.value)}
                  style={{
                    flex: 1,
                    background: '#0F172A',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#F1F5F9',
                    fontSize: '12px',
                    outline: 'none',
                  }}
                />
              </Box>
              {(filterUserId || filterVisitorId) && (
                <Box
                  onClick={clearFilters}
                  sx={{
                    fontSize: '11px',
                    color: '#F59E0B',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: '#FBBF24' },
                  }}
                >
                  Clear filters
                </Box>
              )}
              {activityLoading && (
                <Box sx={{ fontSize: '11px', color: '#94A3B8', marginTop: '8px' }}>
                  Loading...
                </Box>
              )}
            </Box>

            {/* Activity List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '600px', overflow: 'auto' }}>
              {recentActivity.length === 0 ? (
                <Box sx={{ fontSize: '13px', color: '#64748B', textAlign: 'center', padding: '20px' }}>
                  No events found
                </Box>
              ) : (
                recentActivity.map((activity) => (
                  <Box
                    key={activity.id}
                    sx={{
                      background: '#0F172A',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      padding: '12px 16px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#F1F5F9' }}>
                        {activity.eventType === 'page_view' ? '👁️' : '⚡'}
                      </Box>
                      <Box sx={{ fontSize: '13px', fontWeight: 600, color: '#F1F5F9' }}>
                        {activity.eventType.replace('_', ' ')}
                      </Box>
                    </Box>
                    <Box sx={{ fontSize: '12px', color: '#94A3B8', marginBottom: '6px' }}>
                      {activity.path || 'No path'}
                    </Box>

                    {/* User IDs - Clickable */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '4px' }}>
                      <Box
                        onClick={() => handleFilterByVisitor(activity.visitorId)}
                        sx={{
                          fontSize: '10px',
                          color: '#64748B',
                          fontFamily: 'monospace',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#3B82F6',
                            textDecoration: 'underline',
                          },
                        }}
                        title="Click to filter by this visitor"
                      >
                        👤 {activity.visitorId?.substring(0, 8)}...
                      </Box>
                      {activity.userId && (
                        <Box
                          onClick={() => handleFilterByUser(activity.userId)}
                          sx={{
                            fontSize: '10px',
                            color: '#10B981',
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            '&:hover': {
                              color: '#34D399',
                              textDecoration: 'underline',
                            },
                          }}
                          title="Click to filter by this user (authenticated)"
                        >
                          🔐 {activity.userId?.substring(0, 8)}...
                        </Box>
                      )}
                      {activity.ipAddress && (
                        <Box sx={{ fontSize: '10px', color: '#64748B' }}>
                          📍 {activity.ipAddress}
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ fontSize: '11px', color: '#64748B' }}>
                      {new Date(activity.createdAt).toLocaleString()}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const StatCard = ({ stat }) => {
  return (
    <Box
      sx={{
        background: '#1E293B',
        border: `2px solid ${stat.color}`,
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${stat.color}44`,
        },
      }}
    >
      {stat.today !== undefined ? (
        <>
          <Box sx={{ fontSize: '36px', fontWeight: 700, color: stat.color, marginBottom: '8px' }}>
            {stat.today}
          </Box>
          <Box sx={{ fontSize: '13px', fontWeight: 600, color: '#94A3B8', marginBottom: '12px', textTransform: 'uppercase' }}>
            {stat.label}
          </Box>
          <Box sx={{ fontSize: '11px', color: '#64748B' }}>
            Yesterday: {stat.yesterday} · Week: {stat.week}
          </Box>
          <Box sx={{ fontSize: '11px', color: '#64748B' }}>
            Total: {stat.total}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ fontSize: '36px', fontWeight: 700, color: stat.color, marginBottom: '8px' }}>
            {stat.value}
          </Box>
          <Box sx={{ fontSize: '13px', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>
            {stat.label}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AnalyticsTab;
